import type { SlideProps } from "@/engine/app/offsite-config";
import { usePhase, useStore, useWorkshop } from "@/state/store";
import { useSitzungsZahlen } from "@/hooks/useSitzungsZahlen";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";
import { Timer } from "@/components/primitives/Timer";
import { QrCode, zugangsAdresse, zugangsPin } from "./_zugang";

/**
 * Arbeitsfolie. Steht acht Minuten, während das Gremium auf den eigenen
 * Handys promptet. Adresse und PIN müssen aus der letzten Reihe lesbar sein.
 *
 * Diese Folie trägt auch den Timer und die Zahlen der Runde. Die
 * Workshop-Oberfläche übernimmt die Leinwand hier bewusst nicht
 * (`workshopOverlayPhases: ["reveal"]`) — QR-Code und PIN müssen stehen
 * bleiben, damit Nachzügler noch hereinfinden. Deshalb kommt beides auf die
 * Folie statt in ein Overlay.
 */
export function Slide03Prompten(_: SlideProps) {
  const phase = usePhase();
  const { state } = useStore();
  const { timerEndsAt } = useWorkshop().phase;
  const timerRunning = typeof timerEndsAt === "number" && timerEndsAt > Date.now();
  const zahlen = useSitzungsZahlen(
    phase === "waiting" || phase === "working",
    state.auth.token,
  );
  // Während die KI baut, bleibt diese Folie stehen (overnightAnnouncementSlide)
  // und der Moderator zeigt daneben SVO OS. Die Wartezeit soll sichtbar
  // vorangehen, statt als stummes Bild zu stehen.
  const bau = useOvernightStatus(phase === "overnight");

  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />

      {/* Zwei Schritte des Ablaufs zeigen diese Folie: „Warten auf Gäste" und
       *  „Prompten öffnen". Adresse, PIN und QR bleiben beide Male stehen, damit
       *  Nachzügler hereinkommen — aber die Überschrift sagt, was gerade dran
       *  ist. Sonst sähe die Leinwand in Schritt 3 aus wie in Schritt 1. */}
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Jetzt Sie
        </div>
        <h1 className="deck-title rise rise-2" data-testid="zugang-titel">
          {phase === "working"
            ? "Die Fragen sind offen"
            : "Bitte scannen Sie den Code"}
        </h1>
      </header>

      <div className="ar-work">
        <div className="rise rise-3">
          <div className="ar-url">{zugangsAdresse}</div>
          <div className="ar-pin">
            <span className="ar-pin-label">PIN</span>
            <span className="ar-pin-value">{zugangsPin || "— — — —"}</span>
          </div>
          <p className="ar-lede" style={{ marginTop: "var(--s-6)" }}>
            {phase === "working"
              ? "Drei Fragen, eigene Worte. Nehmen Sie sich Zeit — die Uhr läuft noch nicht."
              : "Drei Fragen, eigene Worte. Es gibt keine falsche Antwort."}
          </p>

          {timerRunning && typeof timerEndsAt === "number" && (
            <div className="ar-slide-timer" data-testid="slide-timer">
              <Timer deadline={timerEndsAt} size="side" />
            </div>
          )}

          {phase === "overnight" && (
            <p className="ar-slide-bau" data-testid="slide-bau">
              {bau
                ? bau.status === "complete" ||
                  (bau.total > 0 && bau.done >= bau.total)
                  ? `Fertig — ${bau.done} Prototypen`
                  : `${bau.done} von ${bau.total || "…"} Prototypen fertig`
                : "Die KI baut …"}
            </p>
          )}

          {/* Zahlen der Runde. Beim Warten beruhigt die Zahl den Moderator —
           *  er sieht ohne Nachfragen, ob schon alle drin sind. In der
           *  Arbeitsphase zeigt sie, wie weit das Gremium ist. */}
          {zahlen && (phase === "waiting" || phase === "working") && (
            <p className="ar-slide-zahlen" data-testid="slide-zahlen">
              <strong>{zahlen.angemeldet}</strong>{" "}
              {zahlen.angemeldet === 1 ? "Gast ist da" : "Gäste sind da"}
              {phase === "working" && (
                <>
                  {" · "}
                  <strong>{zahlen.eingereicht}</strong> eingereicht
                </>
              )}
            </p>
          )}
        </div>

        <div className="rise rise-4">
          <QrCode />
          <div className="ar-qr-caption">Mit der Handykamera scannen</div>
        </div>
      </div>
    </div>
  );
}
